import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, type Challenge } from "../lib/api";
import { shareChallenge } from "../lib/share";
import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import Avatar from "../components/Avatar";

// Progress steps for the challenge lifecycle
const PROGRESS_STEPS = ["Created", "Joined", "Resolving", "Settled"];

function getProgressStep(status: Challenge["status"], hasOpponent: boolean): number {
  switch (status) {
    case "OPEN":
      return 0; // Created
    case "LOCKED":
      return 1; // Joined
    case "RESOLVING":
      return 2; // Resolving
    case "SETTLED":
    case "REFUNDED":
      return 3; // Settled/Refunded
    case "EXPIRED":
      return hasOpponent ? 1 : 0;
    default:
      return 0;
  }
}

function getStatusColor(status: Challenge["status"]): string {
  switch (status) {
    case "OPEN":
      return "green";
    case "LOCKED":
      return "yellow";
    case "RESOLVING":
      return "orange";
    case "SETTLED":
      return "blue";
    case "REFUNDED":
    case "EXPIRED":
      return "gray";
    default:
      return "gray";
  }
}

function ChallengeDetail() {
  const { challengeId } = useParams<{ challengeId: string }>();
  const navigate = useNavigate();

  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [userSide, setUserSide] = useState<string | null>(null);
  const [userStake, setUserStake] = useState<number>(0);
  const [userVote, setUserVote] = useState<string | null>(null);
  const [userWon, setUserWon] = useState<boolean | null>(null);
  const [userEarnings, setUserEarnings] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  useTelegramBackButton();

  const fetchChallenge = useCallback(async () => {
    if (!challengeId) return;

    try {
      const response = await api.getChallenge(challengeId);
      setChallenge(response.challenge);
      setUserSide(response.userSide);
      setUserStake(response.userStake);
      setUserVote(response.userVote);
      setUserWon(response.userWon);
      setUserEarnings(response.userEarnings);
    } catch (err) {
      console.error("Failed to fetch challenge:", err);
      setLoadError("Failed to load challenge");
    } finally {
      setIsLoading(false);
    }
  }, [challengeId]);

  useEffect(() => {
    fetchChallenge();
  }, [fetchChallenge]);

  // Poll for updates when in RESOLVING status
  useEffect(() => {
    if (!challenge || challenge.status !== "RESOLVING") return;

    const pollInterval = setInterval(fetchChallenge, 3000);
    return () => clearInterval(pollInterval);
  }, [challenge?.status, fetchChallenge]);

  // Get current user's info
  const currentUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
  const currentUsername = currentUser?.username || "You";
  const currentPhotoUrl = currentUser?.photo_url;

  // Get label for a side
  const getSideLabel = (side: string) => {
    if (!challenge) return side;
    return side === "YES" ? (challenge.labelA || "Yes") : (challenge.labelB || "No");
  };

  // Determine opponent
  const getOpponent = () => {
    if (!challenge || !userSide) return null;

    const oppositeSide = userSide === "YES" ? challenge.noSide : challenge.yesSide;

    const firstParticipant = oppositeSide.participants[0];
    if (firstParticipant) {
      return {
        username: firstParticipant.username,
        photoUrl: firstParticipant.photoUrl,
        stake: firstParticipant.amount,
        side: userSide === "YES" ? "NO" : "YES",
        sideLabel: userSide === "YES" ? (challenge.labelB || "No") : (challenge.labelA || "Yes"),
      };
    }

    return null;
  };

  const opponent = challenge ? getOpponent() : null;
  const hasOpponent = !!opponent;

  const handleShare = () => {
    if (!challenge) return;
    shareChallenge(challenge.id);
  };

  const handleStartResolution = () => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.showConfirm(
        "Start resolution? Both parties will need to vote on the outcome.",
        (confirmed) => {
          if (confirmed) {
            navigate(`/resolve/${challengeId}`);
          }
        }
      );
    } else {
      navigate(`/resolve/${challengeId}`);
    }
  };

  const handleVote = () => {
    navigate(`/resolve/${challengeId}`);
  };

  const handleCancel = async () => {
    if (!challengeId) return;

    const confirmCancel = () => {
      setIsCancelling(true);
      api.cancelChallenge(challengeId)
        .then(() => {
          if (window.Telegram?.WebApp) {
            window.Telegram.WebApp.showAlert("Challenge cancelled. Your contribution has been refunded.", () => {
              navigate("/");
            });
          } else {
            alert("Challenge cancelled. Your contribution has been refunded.");
            navigate("/");
          }
        })
        .catch((err) => {
          console.error("Failed to cancel:", err);
          setActionError(err instanceof Error ? err.message : "Failed to cancel challenge");
        })
        .finally(() => {
          setIsCancelling(false);
        });
    };

    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.showConfirm(
        "Cancel this challenge? Your contribution will be refunded.",
        (confirmed) => {
          if (confirmed) {
            confirmCancel();
          }
        }
      );
    } else if (confirm("Cancel this challenge? Your contribution will be refunded.")) {
      confirmCancel();
    }
  };

  // Check if current user is the creator
  const isCreator = challenge?.creator?.telegramId === currentUser?.id?.toString();

  if (isLoading) {
    return <div className="page loading">Loading...</div>;
  }

  if (loadError || !challenge) {
    return (
      <div className="page error-state">
        <p>{loadError || "Challenge not found"}</p>
        <button className="btn primary" onClick={() => navigate("/")}>
          Go Home
        </button>
      </div>
    );
  }

  const progressStep = getProgressStep(challenge.status, hasOpponent);

  // Check if challenge is time-sensitive (has timer or specific time)
  const hasTimer = !!challenge.timerEndsAt;
  const timerEndsAt = challenge.timerEndsAt ? new Date(challenge.timerEndsAt) : null;
  const now = new Date();
  const timerExpired = timerEndsAt ? timerEndsAt <= now : true;
  const canResolve = !hasTimer || timerExpired;

  // Calculate time remaining for timer
  const getTimeRemaining = () => {
    if (!timerEndsAt) return null;
    const diff = timerEndsAt.getTime() - now.getTime();
    if (diff <= 0) return null;

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // Format resolution/timer end time for display
  const formatResolutionTime = (time: string) => {
    const date = new Date(time);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get timer info text for OPEN challenges
  const getTimerInfoText = () => {
    if (!hasTimer) return null;
    if (challenge.durationMinutes) {
      const hours = challenge.durationMinutes / 60;
      if (hours >= 1) {
        return `${hours}h timer after opponent joins`;
      }
      return `${challenge.durationMinutes}m timer after opponent joins`;
    }
    if (timerEndsAt) {
      return `Resolution at ${formatResolutionTime(challenge.timerEndsAt!)}`;
    }
    return null;
  };

  return (
    <div className="page challenge-detail">
      {/* Action Error Banner */}
      {actionError && (
        <div className="error-banner dismissible" onClick={() => setActionError(null)}>
          {actionError}
          <span className="dismiss">×</span>
        </div>
      )}

      {/* Progress Indicator */}
      <div className="progress-indicator">
        {PROGRESS_STEPS.map((step, index) => (
          <div
            key={step}
            className={`progress-step ${index <= progressStep ? "completed" : ""} ${index === progressStep ? "current" : ""}`}
          >
            <div className="step-dot" />
            <span className="step-label">{step}</span>
          </div>
        ))}
        <div className="progress-line">
          <div
            className="progress-fill"
            style={{ width: `${(progressStep / (PROGRESS_STEPS.length - 1)) * 100}%` }}
          />
        </div>
      </div>

      {/* Matchup Header */}
      <div className="matchup-header">
        <div className="player you">
          <Avatar username={currentUsername} photoUrl={currentPhotoUrl} size="lg" />
          <span className="username">You</span>
          {userSide && (
            <span className={`side-badge ${userSide.toLowerCase()}`}>{getSideLabel(userSide)}</span>
          )}
        </div>

        <div className="vs">VS</div>

        <div className="player opponent">
          {opponent ? (
            <>
              <Avatar username={opponent.username} photoUrl={opponent.photoUrl} size="lg" />
              <span className="username">@{opponent.username}</span>
              <span className={`side-badge ${opponent.side.toLowerCase()}`}>
                {opponent.sideLabel}
              </span>
            </>
          ) : (
            <div className="waiting-opponent">
              <div className="avatar-placeholder">?</div>
              <span>Waiting...</span>
            </div>
          )}
        </div>
      </div>

      {/* Challenge Card */}
      <div className="challenge-card">
        <p className="description">"{challenge.description}"</p>
        <div className="status-row">
          <span className={`status ${getStatusColor(challenge.status)}`}>
            {challenge.status}
          </span>
          <span className="pool">Pool: {challenge.poolTotal} TON</span>
        </div>
      </div>

      {/* Stakes Breakdown */}
      <div className="stakes-card">
        <div className="stake-row">
          <span>Your stake</span>
          <span className="amount">{userStake.toFixed(2)} TON</span>
        </div>
        {opponent && (
          <div className="stake-row">
            <span>@{opponent.username}'s stake</span>
            <span className="amount">{opponent.stake.toFixed(2)} TON</span>
          </div>
        )}
      </div>

      {/* Status-specific Content */}
      {challenge.status === "OPEN" && (
        (() => {
          // Check if challenge has expired (closeTime passed)
          const isExpired = challenge.closeTime && new Date(challenge.closeTime) <= new Date();

          if (isExpired) {
            return (
              <div className="actions-section">
                <p className="status-message expired">This challenge has expired. Refreshing...</p>
              </div>
            );
          }

          return (
            <div className="actions-section">
              <p className="status-message">Waiting for someone to accept your challenge...</p>
              {hasTimer && (
                <div className="timer-info-badge">
                  <span className="timer-icon">⏱️</span>
                  <span>{getTimerInfoText()}</span>
                </div>
              )}
              <button className="btn primary full-width" onClick={handleShare}>
                Share Challenge
              </button>
              {isCreator && (
                <button
                  className="btn secondary full-width"
                  onClick={handleCancel}
                  disabled={isCancelling}
                  style={{ marginTop: "12px" }}
                >
                  {isCancelling ? "Cancelling..." : "Cancel Challenge"}
                </button>
              )}
            </div>
          );
        })()
      )}

      {challenge.status === "LOCKED" && (
        <div className="actions-section">
          {canResolve ? (
            <>
              <p className="status-message ready">Both sides are in! Ready to resolve.</p>
              <button className="btn primary full-width" onClick={handleStartResolution}>
                Start Resolution
              </button>
            </>
          ) : (
            <div className="timer-waiting-section">
              <p className="status-message">Both sides are in! Waiting for timer...</p>
              <div className="countdown-display">
                <span className="countdown-icon">⏱️</span>
                <span className="countdown-time">{getTimeRemaining()}</span>
                <span className="countdown-label">until resolution</span>
              </div>
              {timerEndsAt && (
                <p className="timer-end-time">
                  Resolution available at {formatResolutionTime(timerEndsAt.toISOString())}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {challenge.status === "RESOLVING" && (
        <div className="actions-section">
          {userVote ? (
            <div className="turn-indicator their-turn">
              <p>You voted: <strong>{userVote}</strong></p>
              <p className="waiting-text">Waiting for @{opponent?.username || "opponent"} to vote...</p>
              <div className="spinner-small" />
            </div>
          ) : (
            <div className="turn-indicator your-turn">
              <p className="turn-label">Your turn!</p>
              <button className="btn primary full-width" onClick={handleVote}>
                Cast Your Vote
              </button>
            </div>
          )}
        </div>
      )}

      {challenge.status === "SETTLED" && (
        <div className="outcome-section">
          {userWon ? (
            <div className="result winner">
              <div className="result-icon">🎉</div>
              <h3>You Won!</h3>
              <p className="earnings">+{userEarnings.toFixed(2)} TON</p>
              <p className="outcome-detail">
                The outcome was <strong>{challenge.outcome ? getSideLabel(challenge.outcome) : ""}</strong>
              </p>
            </div>
          ) : (
            <div className="result loser">
              <div className="result-icon">😔</div>
              <h3>You Lost</h3>
              <p className="stake-lost">-{userStake.toFixed(2)} TON</p>
              <p className="outcome-detail">
                The outcome was <strong>{challenge.outcome ? getSideLabel(challenge.outcome) : ""}</strong>
              </p>
            </div>
          )}
        </div>
      )}

      {challenge.status === "REFUNDED" && (
        <div className="outcome-section refunded">
          <div className="result-icon">⚖️</div>
          <h3>Challenge Refunded</h3>
          <p>No agreement was reached on the outcome.</p>
          <p className="refund-amount">{userStake.toFixed(2)} TON returned</p>
        </div>
      )}

      {challenge.status === "EXPIRED" && (
        <div className="outcome-section expired">
          <div className="result-icon">⏰</div>
          <h3>Challenge Expired</h3>
          <p>The challenge expired before it could be completed.</p>
          {userStake > 0 && (
            <p className="refund-amount">{userStake.toFixed(2)} TON returned</p>
          )}
        </div>
      )}

      {/* Home button for completed challenges */}
      {(challenge.status === "SETTLED" || challenge.status === "REFUNDED" || challenge.status === "EXPIRED") && (
        <button className="btn secondary full-width" onClick={() => navigate("/")} style={{ marginTop: "16px" }}>
          Go Home
        </button>
      )}
    </div>
  );
}

export default ChallengeDetail;
